class ShippingAnnounce extends HTMLElement {
  constructor() {
    super();
    this.originalText = this.innerHTML;
    this.observer = null;
    this.initialized = false;

    // Store original prices
    this.isPreorder = this.dataset.preorder === "true";
    this.basePrice = null;
    this.baseComparePrice = null;

    // Bind methods to preserve context
    this.onVariantChangeBound = this.onVariantChange.bind(this);
  }

  connectedCallback() {
    // Store original shipping text
    this.originalText = this.innerHTML;

    // Check if GPO app is already loaded
    this.checkGPOApp();

    // Set up observer for GPO app loading
    if (!this.initialized) {
      this.observeDOMForGPOApp();
    }

    // Listen for variant changes
    if (window.eventBus) {
      window.eventBus.on("variant:change", this.onVariantChangeBound);
    }

    // Initial price capture
    this.updateBasePrices();
  }

  disconnectedCallback() {
    // Clean up
    if (this.observer) {
      this.observer.disconnect();
    }

    if (window.eventBus) {
      window.eventBus.off("variant:change", this.onVariantChangeBound);
    }
  }

  onVariantChange() {
    // When variant changes, update base prices and recalculate with add-ons
    setTimeout(() => {
      // First update the base prices for the new variant
      this.updateBasePrices();

      // Then apply add-ons if any are selected
      const gpoSwatches = document.querySelectorAll(".gpo-app .gpo-swatches");
      if (gpoSwatches.length > 0) {
        this.updateTextBasedOnSelection(gpoSwatches);
        this.updateProductPrice(gpoSwatches);
      }
    }, 100); // Small delay to ensure DOM is updated
  }

  // Capture the base prices of the current variant (without add-ons)
  updateBasePrices() {
    const priceContainer = document.querySelector(".product-single__price");
    if (!priceContainer) return;

    // Get current displayed prices
    const priceElement = priceContainer.querySelector(
      ".product-price-group .money"
    );
    const compareAtPriceElement = priceContainer.querySelector(
      ".compare-at-price .money"
    );

    // Store base prices before any add-ons are applied
    if (priceElement) {
      // Store the initial format as well for reference
      this.basePriceFormat = priceElement.textContent;
      // Parse the price to a number
      this.basePrice = this.parsePrice(priceElement.textContent);
    }

    if (compareAtPriceElement) {
      // Store the initial format as well for reference
      this.baseComparePriceFormat = compareAtPriceElement.textContent;
      // Parse the price to a number
      this.baseComparePrice = this.parsePrice(
        compareAtPriceElement.textContent
      );
    } else {
      // No compare price for this variant
      this.baseComparePrice = null;
      this.baseComparePriceFormat = null;
    }
  }

  checkGPOApp() {
    const gpoSwatches = document.querySelectorAll(".gpo-app .gpo-swatches");
    if (gpoSwatches.length > 0) {
      this.setupSwatchListeners(gpoSwatches);
      this.initialized = true;

      // Disconnect observer
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }
  }

  observeDOMForGPOApp() {
    // Create a MutationObserver to watch for the GPO app to be loaded
    const observer = new MutationObserver(() => {
      this.checkGPOApp();
    });

    // Start observing the document body for DOM changes
    observer.observe(document.body, { childList: true, subtree: true });
    this.observer = observer;
  }

  setupSwatchListeners(gpoSwatches) {
    // Add event listeners to all swatches
    gpoSwatches.forEach((swatchContainer) => {
      const swatchInputs = swatchContainer.querySelectorAll("input");
      swatchInputs.forEach((input) => {
        input.addEventListener("change", () => {
          this.updateTextBasedOnSelection(gpoSwatches);
          this.updateProductPrice(gpoSwatches);
        });
      });
    });

    // Initial check
    this.updateTextBasedOnSelection(gpoSwatches);
    this.updateProductPrice(gpoSwatches);
  }

  updateTextBasedOnSelection(gpoSwatches) {
    // Check if any swatch is selected
    let anySelected = false;

    gpoSwatches.forEach((swatchContainer) => {
      const swatchInputs = swatchContainer.querySelectorAll("input:checked");
      if (swatchInputs.length > 0) {
        anySelected = true;
      }
    });

    if (anySelected || this.isPreorder) {
      // At least one swatch is selected, show the delay text
      this.innerHTML = this.dataset.delayText || this.originalText;
    } else {
      // No swatches selected, show the original text
      this.innerHTML = this.originalText;
    }
  }

  // Calculate total add-on cost from GPO swatches
  getAddonsCost(gpoSwatches) {
    let additionalCost = 0;

    gpoSwatches.forEach((swatchContainer) => {
      const checkedInputs = swatchContainer.querySelectorAll("input:checked");

      checkedInputs.forEach((input) => {
        // Find the price in the label (format: "+ XX,XX€")
        const label = input.nextElementSibling;
        if (label) {
          const priceText = label.querySelector(".addOn");
          if (priceText) {
            // Extract price value (removing the '+ ' prefix and '€' suffix, replace comma with dot)
            const priceString = priceText.textContent
              .trim()
              .replace("+ ", "")
              .replace("€", "")
              .replace(",", ".");

            // Convert to number and add to total
            const price = parseFloat(priceString);
            if (!isNaN(price)) {
              additionalCost += price;
            }
          }
        }
      });
    });

    return additionalCost;
  }

  // Update product price with add-ons
  updateProductPrice(gpoSwatches) {
    // Find price element
    const priceContainer = document.querySelector(".product-single__price");
    if (!priceContainer || this.basePrice === null) return;

    const priceElement = priceContainer.querySelector(
      ".product-price-group .money"
    );
    const compareAtPriceElement = priceContainer.querySelector(
      ".compare-at-price .money"
    );

    if (!priceElement) return;

    // Calculate total additional cost
    const additionalCost = this.getAddonsCost(gpoSwatches);

    // Calculate and update prices
    if (additionalCost > 0) {
      // Update regular price: base price + add-ons
      const newPrice = this.basePrice + additionalCost;
      priceElement.textContent = this.formatPrice(
        newPrice,
        this.basePriceFormat
      );

      // Update compare price if it exists
      if (compareAtPriceElement && this.baseComparePrice !== null) {
        const newComparePrice = this.baseComparePrice + additionalCost;
        compareAtPriceElement.textContent = this.formatPrice(
          newComparePrice,
          this.baseComparePriceFormat
        );
      }
    } else {
      // Reset to base prices (no add-ons selected)
      priceElement.textContent = this.basePriceFormat;

      if (compareAtPriceElement && this.baseComparePriceFormat) {
        compareAtPriceElement.textContent = this.baseComparePriceFormat;
      }
    }
  }

  // Helper to parse price string to number
  parsePrice(priceString) {
    // Remove currency symbol and non-numeric characters except decimal separator
    return parseFloat(priceString.replace(/[^\d,\.]/g, "").replace(",", "."));
  }

  // Helper to format price with the same format as the original
  formatPrice(price, originalPriceString) {
    // Check if the original price used comma as decimal separator
    const useComma = originalPriceString.includes(",");

    // Format the number to 2 decimal places
    let formatted = price.toFixed(2);

    // Replace decimal point with comma if needed
    if (useComma) {
      formatted = formatted.replace(".", ",");
    }

    // Add the currency symbol/code in the same position as the original
    if (
      originalPriceString.startsWith("€") ||
      originalPriceString.startsWith("$")
    ) {
      return originalPriceString.charAt(0) + formatted;
    } else if (
      originalPriceString.endsWith("€") ||
      originalPriceString.endsWith("$")
    ) {
      return (
        formatted + originalPriceString.charAt(originalPriceString.length - 1)
      );
    } else {
      // If no obvious currency symbol position, just return the number
      return formatted + "€";
    }
  }
}

// Register the custom element
customElements.define("shipping-announce", ShippingAnnounce);
